/**
 * CSV import/export helpers for bulk page creation.
 *
 * One CSV row = one page. Nested content (grid-guide categories→cards,
 * listicle picks→bullets, the comparison table) is packed into a single cell
 * using a small, documented delimiter grammar:
 *
 *   |   between list items      (categories, picks, table rows, badges)
 *   ::  splits a category label from its cards          (grid-guide only)
 *   ~   between sub-items       (cards in a category, bullets in a pick)
 *   ;   between key=value fields inside a card/pick, or cells inside a table row
 *   =   splits a field key from its value  (only the first = is used, so URLs are safe)
 *
 * These 5 characters therefore cannot appear inside the content itself.
 * Everything can still be fixed visually in the editor after upload, so the
 * CSV only needs to get "close enough".
 */

import { buildInitial } from "../pages/PageForm";

export const PAGE_TYPES = ["grid-guide", "listicle"];

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const URL_RE = /^https?:\/\/.+/i;
const SLUG_RE = /^[a-z0-9.-]+$/;

/* ────────────────────────────────────────────────────────────────────
 * Low-level RFC-4180 CSV parsing / stringifying
 * ──────────────────────────────────────────────────────────────────── */

/** Parse CSV text into an array of rows (each row an array of string cells). */
export function parseCsv(text) {
  if (!text) return [];
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1); // strip BOM

  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === ",") {
      row.push(field);
      field = "";
      i++;
      continue;
    }
    if (c === "\r") {
      i++;
      continue;
    }
    if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      i++;
      continue;
    }
    field += c;
    i++;
  }
  row.push(field);
  rows.push(row);

  // Drop rows that are entirely empty (trailing newline, blank lines).
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

/** Quote a single CSV cell if it contains a comma, quote or newline. */
function csvCell(value) {
  const s = value == null ? "" : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Build CSV text from a header array + array of row arrays. */
export function toCsv(header, rows) {
  const lines = [header.map(csvCell).join(",")];
  for (const r of rows) lines.push(r.map(csvCell).join(","));
  return lines.join("\r\n") + "\r\n";
}

/* ────────────────────────────────────────────────────────────────────
 * Nested-cell encode / decode
 * ──────────────────────────────────────────────────────────────────── */

const kv = (pairs) =>
  pairs
    .filter(([, v]) => v != null && String(v).trim() !== "")
    .map(([k, v]) => `${k}=${String(v).trim()}`)
    .join(";");

const parsePairs = (str) => {
  const out = {};
  String(str)
    .split(";")
    .forEach((part) => {
      const t = part.trim();
      if (!t) return;
      const eq = t.indexOf("=");
      if (eq === -1) return;
      const key = t.slice(0, eq).trim();
      const val = t.slice(eq + 1).trim();
      if (key) out[key] = val;
    });
  return out;
};

// -- categories (grid-guide) --
export function encodeCategories(categories = []) {
  return categories
    .map((cat) => {
      const cards = (cat.cards || [])
        .map((card) =>
          kv([
            ["title", card.title],
            ["tag", card.tag],
            ["img", card.img],
            ["url", card.url],
            ["desc", card.description],
          ])
        )
        .join(" ~ ");
      return `${cat.label || ""}::${cards}`;
    })
    .join(" | ");
}

export function decodeCategories(str) {
  return String(str)
    .split("|")
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const sep = chunk.indexOf("::");
      const label = (sep === -1 ? chunk : chunk.slice(0, sep)).trim();
      const cardsStr = sep === -1 ? "" : chunk.slice(sep + 2);
      const cards = cardsStr
        .split("~")
        .map((c) => c.trim())
        .filter(Boolean)
        .map((c) => {
          const p = parsePairs(c);
          return {
            title: p.title || "",
            tag: p.tag || "Brand",
            img: p.img || "",
            url: p.url || "",
            description: p.desc || p.description || "",
          };
        });
      return { label, cards };
    });
}

// -- picks (listicle) --
export function encodePicks(picks = []) {
  return picks
    .map((pick) =>
      kv([
        ["title", pick.title],
        ["badge", pick.badge],
        ["price", pick.price],
        ["url", pick.url],
        ["img", pick.img],
        ["desc", pick.description],
        ["take", pick.take],
        ["bullets", (pick.bullets || []).filter(Boolean).join("~")],
      ])
    )
    .join(" | ");
}

export function decodePicks(str, defaultTake = "") {
  return String(str)
    .split("|")
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const p = parsePairs(chunk);
      const bullets = (p.bullets || "")
        .split("~")
        .map((b) => b.trim())
        .filter(Boolean);
      return {
        title: p.title || "",
        badge: p.badge || "",
        price: p.price || "",
        url: p.url || "",
        img: p.img || "",
        description: p.desc || p.description || "",
        take: p.take || defaultTake || "",
        bullets,
      };
    });
}

// -- table rows (listicle) --
export function encodeTableRows(tableRows = []) {
  return tableRows.map((r) => (r.cells || []).join(";")).join(" | ");
}

export function decodeTableRows(str) {
  return String(str)
    .split("|")
    .map((row) => row.trim())
    .filter(Boolean)
    .map((row) => ({ cells: row.split(";").map((c) => c.trim()) }));
}

// -- badges (listicle) --
export function encodeBadges(badges = []) {
  return badges.filter(Boolean).join(" | ");
}
export function decodeBadges(str) {
  return String(str)
    .split("|")
    .map((b) => b.trim())
    .filter(Boolean);
}

/* ────────────────────────────────────────────────────────────────────
 * Column definitions per type
 * ──────────────────────────────────────────────────────────────────── */

const COMMON_COLUMNS = [
  "slug",
  "type",
  "title",
  "subtitle",
  "eyebrow",
  "metaTitle",
  "canonicalSlug",
  "date",
  "affiliateUrl",
  "themeStart",
  "themeEnd",
  "accent",
];

const TYPE_COLUMNS = {
  "grid-guide": [...COMMON_COLUMNS, "categories"],
  listicle: [
    ...COMMON_COLUMNS,
    "metaBadges",
    "howWePickedTitle",
    "howWePickedText",
    "defaultEditorTake",
    "picks",
    "tableTitle",
    "tableRows",
    "verdictTitle",
    "verdictText",
    "verdictButtonText",
  ],
};

export const columnsFor = (type) => TYPE_COLUMNS[type] || COMMON_COLUMNS;

/** Short human descriptions per column, used by the help modal. */
export const COLUMN_HELP = {
  slug: "Required. Lowercase letters/numbers/dots/hyphens. Unique. Becomes /stores/<slug>.",
  type: "Required. grid-guide or listicle.",
  title: "Required. Main hero heading.",
  subtitle: "Hero sub-heading.",
  eyebrow: "Small kicker text above the title.",
  metaTitle: "Optional <title> override for SEO.",
  canonicalSlug: "Optional store slug for the canonical URL.",
  date: "Display date (YYYY-MM-DD). Defaults to today if blank.",
  affiliateUrl: "Default outbound link for cards/CTAs without their own URL.",
  themeStart: "Hero gradient start colour (#hex).",
  themeEnd: "Hero gradient end colour (#hex).",
  accent: "Accent colour (#hex).",
  categories:
    "Categories & cards. Format:  Label::card~card | Label::card  where each card is title=..;tag=..;img=..;url=..;desc=..",
  metaBadges: "Review badges separated by | e.g. Editor Reviewed | Value Scored",
  howWePickedTitle: "'How We Picked' heading.",
  howWePickedText: "'How We Picked' paragraph.",
  defaultEditorTake: "Default Editor's Take applied to picks with no take.",
  picks:
    "Ranked products separated by |. Each product is title=..;badge=..;price=..;url=..;img=..;desc=..;take=..;bullets=a~b~c",
  tableTitle: "Comparison table heading.",
  tableRows:
    "Comparison table. Rows separated by |, cells by ; e.g. Brand;Best For;Price | Brand A;Value;$19",
  verdictTitle: "Final verdict heading.",
  verdictText: "Final verdict paragraph.",
  verdictButtonText: "Verdict CTA button label.",
};

/** The 5 nesting delimiters, for the help modal legend. */
export const NESTING_LEGEND = [
  ["|", "Separates list items (categories, products, table rows, badges)."],
  ["::", "Separates a category's label from its cards (grid-guide)."],
  ["~", "Separates sub-items (cards in a category, bullets in a product)."],
  [";", "Separates key=value fields in a card/product, or cells in a table row."],
  ["=", "Separates a field key from its value (only the first = counts, so URLs are safe)."],
];

/* ────────────────────────────────────────────────────────────────────
 * Demo rows + template
 * ──────────────────────────────────────────────────────────────────── */

const today = () => new Date().toISOString().split("T")[0];

const DEMO = {
  "grid-guide": {
    slug: "best-sunglasses-round-face",
    type: "grid-guide",
    title: "The Best Sunglasses For Round Faces",
    subtitle: "Curated frames that add definition and balance to rounder features.",
    eyebrow: "PERSONALIZED STYLE GUIDE • 2026",
    metaTitle: "Best Sunglasses for Round Faces (2026)",
    canonicalSlug: "zennioptical.com",
    date: today(),
    affiliateUrl: "https://example.com/aff?ref=demo",
    themeStart: "#61a99f",
    themeEnd: "#3d6e68",
    accent: "#61a99f",
    categories:
      "Round::title=Classic Aviator;tag=Ray-Ban;img=https://img.example.com/aviator.jpg;url=https://shop.example.com/aviator;desc=Angular lines that complement round faces ~ title=Bold Wayfarer;tag=Ray-Ban;img=https://img.example.com/wayfarer.jpg;url=;desc=A timeless everyday frame | Square::title=Sharp Clubmaster;tag=Ray-Ban;img=https://img.example.com/clubmaster.jpg;url=;desc=Structured browline for extra definition",
  },
  listicle: {
    slug: "top-wireless-earbuds",
    type: "listicle",
    title: "10 Wireless Earbuds Everyone Is Loving",
    subtitle: "We researched the earbuds dominating wishlists this season.",
    eyebrow: "EDITOR'S GUIDE • 2026",
    metaTitle: "Best Wireless Earbuds (2026)",
    canonicalSlug: "bestbuy.com",
    date: today(),
    affiliateUrl: "https://example.com/aff?ref=demo",
    themeStart: "#61a99f",
    themeEnd: "#3d6e68",
    accent: "#61a99f",
    metaBadges: "Editor Reviewed | Value Scored | Sound Tested",
    howWePickedTitle: "How We Picked",
    howWePickedText: "We weighed sound quality, battery life, comfort and price.",
    defaultEditorTake: "A strong balance of value and performance.",
    picks:
      "title=AeroBuds Pro;badge=Best Overall;price=$79;url=https://shop.example.com/aerobuds;img=https://img.example.com/aerobuds.jpg;desc=Balanced sound with punchy bass;take=Our top pick for most people;bullets=8hr battery~Active noise cancel~USB-C | title=SoundLite Mini;badge=Best Value;price=$39;url=;img=https://img.example.com/soundlite.jpg;desc=Great everyday earbuds on a budget;take=Hard to beat under $40;bullets=Comfort fit~Sweat resistant",
    tableTitle: "Quick Comparison",
    tableRows: "Brand;Best For;Price | AeroBuds Pro;Best Overall;$79 | SoundLite Mini;Best Value;$39",
    verdictTitle: "Our Final Verdict",
    verdictText:
      "For most listeners the AeroBuds Pro offer the best mix of price and performance.",
    verdictButtonText: "Shop Now",
  },
};

export const demoRowObject = (type) => DEMO[type] || DEMO["grid-guide"];

/** Build the downloadable template CSV (header + one worked demo row). */
export function buildTemplate(type) {
  const cols = columnsFor(type);
  const demo = demoRowObject(type);
  const row = cols.map((c) => demo[c] ?? "");
  return toCsv(cols, [row]);
}

/* ────────────────────────────────────────────────────────────────────
 * Row → page object
 * ──────────────────────────────────────────────────────────────────── */

function rowToPage(raw, expectedType) {
  const type = (raw.type || "").trim() || expectedType;
  const page = buildInitial(type === "listicle" ? "listicle" : "grid-guide");
  page.type = type;

  const setIf = (k) => {
    if (raw[k] != null && String(raw[k]).trim() !== "") page[k] = String(raw[k]).trim();
  };

  page.slug = (raw.slug || "").trim().toLowerCase().replace(/\s+/g, "-");
  page.title = (raw.title || "").trim();
  page.metaTitle = (raw.metaTitle || "").trim();
  page.canonicalSlug = (raw.canonicalSlug || "").trim();

  setIf("subtitle");
  setIf("eyebrow");
  setIf("date");
  setIf("affiliateUrl");
  setIf("themeStart");
  setIf("themeEnd");
  setIf("accent");

  if (type === "grid-guide") {
    page.categories =
      raw.categories && raw.categories.trim() ? decodeCategories(raw.categories) : [];
  } else if (type === "listicle") {
    setIf("howWePickedTitle");
    setIf("howWePickedText");
    setIf("defaultEditorTake");
    setIf("tableTitle");
    setIf("verdictTitle");
    setIf("verdictText");
    setIf("verdictButtonText");
    page.metaBadges =
      raw.metaBadges && raw.metaBadges.trim() ? decodeBadges(raw.metaBadges) : [];
    page.picks =
      raw.picks && raw.picks.trim() ? decodePicks(raw.picks, page.defaultEditorTake) : [];
    page.tableRows =
      raw.tableRows && raw.tableRows.trim() ? decodeTableRows(raw.tableRows) : [];
  }

  return page;
}

/* ────────────────────────────────────────────────────────────────────
 * Validation
 * ──────────────────────────────────────────────────────────────────── */

/** Returns an array of human-readable error strings (empty === valid). */
export function validatePage(page, seenSlugs) {
  const errors = [];

  if (!page.slug) {
    errors.push("Slug is required.");
  } else if (!SLUG_RE.test(page.slug)) {
    errors.push("Slug may only contain lowercase letters, numbers, dots and hyphens.");
  } else if (seenSlugs && seenSlugs.has(page.slug)) {
    errors.push("Duplicate slug — another row in this file uses the same slug.");
  }

  if (!PAGE_TYPES.includes(page.type)) {
    errors.push(`Unknown type "${page.type}". Use grid-guide or listicle.`);
  }

  if (!page.title || !page.title.trim()) {
    errors.push("Title is required.");
  }

  [
    ["themeStart", page.themeStart],
    ["themeEnd", page.themeEnd],
    ["accent", page.accent],
  ].forEach(([name, val]) => {
    if (val && !HEX_RE.test(val)) errors.push(`${name} must be a hex colour like #61a99f.`);
  });

  if (page.affiliateUrl && !URL_RE.test(page.affiliateUrl)) {
    errors.push("affiliateUrl must start with http:// or https://.");
  }

  if (page.date && !/^\d{4}-\d{2}-\d{2}$/.test(page.date)) {
    errors.push("date must be in YYYY-MM-DD format.");
  }

  if (page.type === "grid-guide") {
    if (!page.categories || page.categories.length === 0) {
      errors.push("At least one category is required (categories column).");
    } else {
      page.categories.forEach((cat, i) => {
        if (!cat.label || !cat.label.trim())
          errors.push(`Category ${i + 1}: label is missing.`);
        if (!cat.cards || cat.cards.length === 0)
          errors.push(`Category ${i + 1} (${cat.label || "?"}): needs at least one card.`);
        (cat.cards || []).forEach((card, ci) => {
          if (!card.title || !card.title.trim())
            errors.push(`Category ${i + 1} card ${ci + 1}: title is missing.`);
          if (card.url && !URL_RE.test(card.url))
            errors.push(`Category ${i + 1} card ${ci + 1}: url must start with http(s)://.`);
        });
      });
    }
  }

  if (page.type === "listicle") {
    if (!page.picks || page.picks.length === 0) {
      errors.push("At least one product is required (picks column).");
    } else {
      page.picks.forEach((pick, i) => {
        if (!pick.title || !pick.title.trim())
          errors.push(`Product ${i + 1}: title is missing.`);
        if (pick.url && !URL_RE.test(pick.url))
          errors.push(`Product ${i + 1}: url must start with http(s)://.`);
      });
    }
    if (page.tableRows && page.tableRows.length > 0) {
      const width = page.tableRows[0].cells.length;
      page.tableRows.forEach((r, i) => {
        if (r.cells.length !== width)
          errors.push(`Table row ${i + 1}: has ${r.cells.length} cells but header has ${width}.`);
      });
    }
  }

  return errors;
}

/* ────────────────────────────────────────────────────────────────────
 * Top-level: CSV text → parsed page rows
 * ──────────────────────────────────────────────────────────────────── */

/**
 * Parse CSV text into a list of { page, errors } entries plus a fatal
 * `error` string when the file itself is unusable.
 *
 * @param {string} text        raw CSV text
 * @param {string} expectedType the type selected in the UI (used when a row
 *                               leaves the `type` column blank / for the
 *                               expected header set)
 */
export function parseCsvToPages(text, expectedType) {
  const rows = parseCsv(text);
  if (rows.length === 0) return { error: "The file is empty.", entries: [] };

  const header = rows[0].map((h) => h.trim());
  const dataRows = rows.slice(1);
  if (dataRows.length === 0) {
    return { error: "No data rows found — only the header is present.", entries: [] };
  }

  const knownCols = new Set(columnsFor(expectedType));
  const missingRequired = ["slug", "title"].filter((c) => !header.includes(c));
  if (missingRequired.length) {
    return {
      error: `Missing required column(s): ${missingRequired.join(", ")}. Download a fresh template.`,
      entries: [],
    };
  }
  const unknown = header.filter((h) => h && !knownCols.has(h));

  const seenSlugs = new Set();
  const entries = dataRows.map((cells, idx) => {
    const raw = {};
    header.forEach((h, i) => {
      if (h) raw[h] = cells[i] ?? "";
    });
    const page = rowToPage(raw, expectedType);
    const errors = validatePage(page, seenSlugs);
    if (page.slug) seenSlugs.add(page.slug);
    return { rowNumber: idx + 2, page, errors };
  });

  return {
    error: null,
    warning: unknown.length ? `Ignored unknown column(s): ${unknown.join(", ")}.` : null,
    entries,
  };
}

/** Trigger a browser download of `content` as a .csv file. */
export function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
